import { useCallback, useContext } from "react";
import { UI } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./InvoiceAccountings.module.css";
import { InvoiceAccounting } from "./accounting/InvoiceAccounting";
import { LOADING_TYPE, LOADING_TYPE_ACCOUNTING } from "src/stores/krediflow/InvoiceEnums";
import { completeShorthandYear, completeShorthandYearWhileTyping } from "src/utils/Date";
import { InvoiceAccountingsReadOnlyView } from "./readonly/InvoiceAccountingsReadOnly";
import { RealestateChangedWarningModal } from "./accounting/RealestateChangedWarningModal/RealestateChangedWarningModal";
import { RealestateField } from "./accounting/RealestateField";
import { ErpType } from "src/network/User";

const InvoiceAccoutingsBase = () => {
    const { t } = useTranslation();
    const { invoiceStore, authStore } = useContext(RootStoreContext);

    const onChangeAccountingDate = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            invoiceStore.updateAccountingWithBookingDate(completeShorthandYearWhileTyping(e.target.value));
        },
        [invoiceStore]
    );

    const completeAccountingDateYear = useCallback(
        (enteredDate: string) => {
            const completedDate = completeShorthandYear(enteredDate);

            if (completedDate !== enteredDate) {
                invoiceStore.updateAccountingWithBookingDate(completedDate);
            }
        },
        [invoiceStore]
    );

    const onBlurAccountingDate = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            completeAccountingDateYear(e.target.value);
        },
        [completeAccountingDateYear]
    );

    // the shorthand year has to be completed before Enter triggers an action on the invoice
    const onKeyDownAccountingDate = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                completeAccountingDateYear(e.currentTarget.value);
            }
        },
        [completeAccountingDateYear]
    );

    const addAccountingRow = useCallback(() => {
        invoiceStore.currentInvoice?.addAccountingRow();
    }, [invoiceStore]);

    const addAccountingButtonLabel = (
        <div className="d-flex align-items-center">
            <UI.Icon icon={UI.SVGIcon.Plus} color={"white"} size={25} />
            <div>{t("screens.kredi_flow.accountings.add_accounting")}</div>
        </div>
    );

    const saveAccoutingsButtonLabel = (
        <div className="d-flex align-items-center">
            <div>{t("labels.save")}</div>
            <UI.RotatingSpinner
                className={`${styles.AccountingSaveButtonSpinner} ${
                    invoiceStore.loadingTypeAccounting !== LOADING_TYPE_ACCOUNTING.SAVING_ACCOUNTINGS ? "d-none" : ""
                }`}
                noLogo={true}
                size={20}
            />
        </div>
    );

    const saveAccountingsButton = (
        <UI.Button
            onClick={invoiceStore.validateAndSaveInvoiceAccountings(true)}
            className={styles.SaveButton}
            label={saveAccoutingsButtonLabel}
            disabled={invoiceStore.loadingTypeAccounting !== LOADING_TYPE_ACCOUNTING.NONE}
        />
    );

    const onDiscardAccountingChanges = useCallback(() => {
        invoiceStore.discardCurrentInvoiceAccountingsChanges();
    }, [invoiceStore]);

    const onClickEditButton = useCallback(() => {
        // Switches to edit mode AND loads the tag suggestions for the existing rows (once).
        invoiceStore.startAccountingEditing();
    }, [invoiceStore]);

    const titleRow = (
        <UI.Row>
            <div className={`${styles.InvoiceAccountingsTitle} mb-3`}>
                {t("screens.kredi_flow.accountings.section_title")}
            </div>
            <div className="ml-auto" style={{ paddingRight: "15px" }}>
                {invoiceStore.isAccountingEditing ? (
                    saveAccountingsButton
                ) : (
                    <UI.Button
                        onClick={onClickEditButton}
                        className={`${styles.EditButton} `}
                        label={t("labels.edit")}
                        disabled={invoiceStore.isInvoiceLocked}
                    />
                )}
            </div>
        </UI.Row>
    );

    const accountingsEditViewRows = (
        <>
            <UI.Row>
                <UI.Col lg={2}>
                    <UI.Input
                        label={`${t("screens.kredi_flow.accountings.date").toString()} *`}
                        type="date"
                        autoComplete="off"
                        min={"2000-01-01"}
                        max={"2100-12-31"}
                        value={
                            invoiceStore.currentInvoice && invoiceStore.currentInvoice.accountingDate
                                ? invoiceStore.currentInvoice.accountingDate
                                : ""
                        }
                        onChange={onChangeAccountingDate}
                        onBlur={onBlurAccountingDate}
                        onKeyDown={onKeyDownAccountingDate}
                    />
                    {invoiceStore.currentInvoice?.accountingValidationErrors[0] &&
                        invoiceStore.currentInvoice?.accountingValidationErrors[0].isDateRangeValid === false && (
                            <span className={styles.DateError}>
                                {t("screens.kredi_flow.accountings.error.invalid_date_range")}
                            </span>
                        )}
                </UI.Col>
                {authStore.user?.erpType === ErpType.IT2 && (
                    <UI.Col lg={5}>
                        <RealestateField />
                    </UI.Col>
                )}
            </UI.Row>

            {/* ACCOUNTING ROWS */}
            {invoiceStore.currentInvoice &&
            invoiceStore.currentInvoice.accountings &&
            invoiceStore.currentInvoice.accountings.length > 0
                ? invoiceStore.currentInvoice.accountings.map((accounting, index) => (
                      <InvoiceAccounting key={index} accounting={accounting} accountingIndex={index} />
                  ))
                : null}

            {!invoiceStore.currentInvoice?.totalAccountingAmountMatchesInvoiceAmount ? (
                <div className={`error-text ${styles.AccountingAmountErrorMessage}`}>
                    <span>{t("screens.kredi_flow.accountings.error.accounting_amounts_dont_match")}</span>
                </div>
            ) : (
                <></>
            )}

            <UI.Row>
                <UI.Button
                    onClick={addAccountingRow}
                    className={`${styles.AddAccountingRowButton} my-4 ml-2`}
                    label={addAccountingButtonLabel}
                />
            </UI.Row>
            <UI.Row className={`${styles.AccountingsTotalAmountRow} align-content-center`}>
                <UI.Col lg={{ span: 2, offset: 10 }}>
                    <div className={"d-flex"}>
                        <div className={styles.AccountingsTotalAmountText} style={{ width: "84%" }}>
                            <span>{t("screens.kredi_flow.total")}</span>
                            <span className="ml-3">{invoiceStore.currentInvoice?.amount?.toFixed(2)}</span>
                        </div>
                        <div style={{ width: "16%" }}></div>
                    </div>
                </UI.Col>
            </UI.Row>
            <UI.Row className="mt-3">
                <UI.Col className={"flex-grow-0"}>{saveAccountingsButton}</UI.Col>
                <UI.Col>
                    <UI.Button
                        label={t("labels.discard")}
                        disabled={
                            !invoiceStore.currentInvoice?.hasChangesAccounting ||
                            invoiceStore.loadingTypeAccounting === LOADING_TYPE_ACCOUNTING.SAVING_ACCOUNTINGS
                        }
                        onClick={onDiscardAccountingChanges}
                        className={styles.DiscardButton}
                        variant="outline-primary"
                    />
                </UI.Col>
            </UI.Row>
        </>
    );
    return invoiceStore.loadingType === LOADING_TYPE.INITIALIZING ? (
        <div>
            <UI.RotatingSpinner noLogo size={40} />
            <div className="mt-3 text-center">{t("screens.kredi_flow.loading_accountings")}</div>
        </div>
    ) : (
        <>
            {<RealestateChangedWarningModal />}
            {titleRow}
            {invoiceStore.isAccountingEditing ? accountingsEditViewRows : <InvoiceAccountingsReadOnlyView />}
        </>
    );
};

export const InvoiceAccountings = Sentry.withProfiler(observer(InvoiceAccoutingsBase));
